/**
 * Teklife özel otel satış fiyatı (02.11.2026), SAF mantık: sunucu (PDF ve mesaj) ve ekran aynı
 * fonksiyonu çalıştırır, böylece ekranda görülen toplam ile PDF'teki toplam ayrışamaz.
 *
 * BU DOSYA HİÇBİR YEREL DOSYAYI İÇE AKTARMAZ, bilerek: ekran (`web/`) onu doğrudan okuyor ve API'nin
 * dekoratörlü dosyalarından birine değen her içe aktarma web derlemesini kırıyor (ölçüldü, pre-push
 * kapısı). Tipler yapısal ve en küçük hâliyle burada; sunucudaki ve ekrandaki `OdaGrubu` buna uyar.
 *
 * Sistem fiyatı = tedarikçi maliyeti + %10. Satışçı Booking'e bakıp teklif için değiştirir
 * (ör. 1.650 USD yerine 1.850 USD). Katalog ve fiyat işi değişmez; bu teklifin kendi kopyasında
 * geçerli bir örtüdür (`paket-duzenleme.ts` ile aynı ilke).
 *
 * Seçilen odaların toplamı `hedefMinor`a eşitlenir ve her odanın satırı aynı oranda ölçeklenir,
 * yoksa oda satırları toplamla tutmazdı. Yuvarlama farkı son odaya yazılır: satırlar ile toplam
 * HER ZAMAN tam eşit. Para birimi, geçerlilik süresi ve iptal koşulu aynı kalır.
 */

/** Bir otel seçeneğinin teklife özel satış toplamı için üst sınır (minor birim). */
export const OTEL_FIYAT_DUZENLEME_AZAMI = 100_000_000;

interface OlcekSecenegi {
  readonly secenekNo: string;
  readonly toplamMinor: number;
  readonly tekOdaMinor: number;
  readonly paraBirimi: string;
}
interface OlcekGrubu<S extends OlcekSecenegi> {
  readonly anahtar: string;
  readonly adet: number;
  readonly secenekler: readonly S[];
  readonly varsayilanSecenekNo: string | null;
}

export type OlcekSonucu<O> =
  | { readonly tamam: true; readonly otel: O }
  | { readonly tamam: false; readonly neden: 'GECERSIZ' | 'PARA_FARKLI' | 'SECIM_EKSIK' | 'COK_DUSUK' };

export function otelFiyatiniOlcekle<S extends OlcekSecenegi, G extends OlcekGrubu<S>, O extends { readonly odaGruplari: readonly G[]; readonly enUcuzMinor: number | null }>(
  otel: O,
  secim: Readonly<Record<string, string>> | undefined,
  hedefMinor: number,
): OlcekSonucu<O> {
  if (!Number.isSafeInteger(hedefMinor) || hedefMinor < 1 || hedefMinor > OTEL_FIYAT_DUZENLEME_AZAMI) return { tamam: false, neden: 'GECERSIZ' };
  const secilenler: { grup: G; secenek: S }[] = [];
  for (const g of otel.odaGruplari) {
    const no = secim?.[g.anahtar] ?? g.varsayilanSecenekNo;
    const secenek = no === null ? undefined : g.secenekler.find((x) => x.secenekNo === no);
    if (secenek === undefined) return { tamam: false, neden: 'SECIM_EKSIK' };
    secilenler.push({ grup: g, secenek });
  }
  if (secilenler.length === 0) return { tamam: false, neden: 'SECIM_EKSIK' };
  if (new Set(secilenler.map((x) => x.secenek.paraBirimi)).size > 1) return { tamam: false, neden: 'PARA_FARKLI' };
  const eskiToplam = secilenler.reduce((t, x) => t + x.secenek.toplamMinor, 0);
  if (eskiToplam <= 0) return { tamam: false, neden: 'SECIM_EKSIK' };
  let dagitilan = 0;
  const yeniToplam = new Map<string, { secenekNo: string; toplam: number }>();
  for (const [i, s] of secilenler.entries()) {
    const son = i === secilenler.length - 1;
    const pay = son ? hedefMinor - dagitilan : Math.round((hedefMinor * s.secenek.toplamMinor) / eskiToplam);
    if (pay < 1) return { tamam: false, neden: 'COK_DUSUK' };
    dagitilan += pay;
    yeniToplam.set(s.grup.anahtar, { secenekNo: s.secenek.secenekNo, toplam: pay });
  }
  const odaGruplari = otel.odaGruplari.map((g): G => {
    const yeni = yeniToplam.get(g.anahtar);
    if (yeni === undefined) return g;
    return { ...g, secenekler: g.secenekler.map((o) => (o.secenekNo === yeni.secenekNo ? { ...o, toplamMinor: yeni.toplam, tekOdaMinor: Math.round(yeni.toplam / Math.max(1, g.adet)) } : o)) };
  });
  return { tamam: true, otel: { ...otel, odaGruplari, enUcuzMinor: hedefMinor } };
}
